import React, { useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { date } from 'utils'
import { inventoryStatusFromCapacity } from 'utils/inventory'
import { INVENTORY_STATUSES, AUTO_ORDER_STATUSES } from 'constants/statuses'
import { useReferrerState } from 'hooks'
import { useTagProduct } from 'hooks/api/useTagProduct'
import { useVeraProductsByTag, useTagInventoryThreshold, useBulkUnassignDefaultTag } from 'hooks/api/useTagInventory'
import { ROUTES } from 'routes'

import { InfoIcon, PlusIcon, Settings2Icon, ShoppingCartIcon, ShoppingBagIcon, CheckIcon, XIcon, Link2OffIcon, ArrowUpRightIcon } from 'lucide-react'
import { Box, Stack, Typography, Divider, Tooltip, useTheme } from '@mui/material'

import { Button, Skeleton, Modal } from '@repo/ui'
import {
  Header,
  ActionDropdown,
  EmptyState,
  InfoBlock,
  InfoItem,
  Table,
  StatusChipCell,
  ProductCell,
  CategoryCell,
  InventoryLevelCell,
  SetInventorySettingsModal,
  SetProductsForTagModal,
  PermissionGate,
  type ActionDropdownItem,
} from 'components'
import { Inventory } from '@mui/icons-material'

export const TagInventoryPage = () => {
  const { sku } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  // Detail → detail: the derived label would be the Inventory list, not this tag.
  const referrer = useReferrerState('Tag inventory')

  const [openInventorySettingsModal, setOpenInventorySettingsModal] = useState(false)
  const [openSetProductsForTagModal, setOpenSetProductsForTagModal] = useState(false)
  // Row pending unassign confirmation (destructive action → confirm modal).
  const [unassignTarget, setUnassignTarget] = useState<any | null>(null)

  // Step 1: look up the tag shop product by SKU to get its UUID
  const { data: tagProduct, isLoading: isLoadingTag } = useTagProduct(
    { sku: sku as string },
    { enabled: !!sku },
  )

  const tagShopProductId = tagProduct?.id ?? null

  // Step 2: fetch Vera products assigned to this tag + threshold
  const { data: veraProductsData, isLoading: isLoadingProducts, refetch: refetchProducts } = useVeraProductsByTag(
    { tagShopProductId: tagShopProductId ?? '' },
    { enabled: !!tagShopProductId },
  )
  const { data: threshold, isLoading: isLoadingThreshold } = useTagInventoryThreshold(
    { tagShopProductId: tagShopProductId ?? '' },
    { enabled: !!tagShopProductId },
  )

  const products = veraProductsData?.products ?? []
  const refetch = refetchProducts

  const { mutate: unassignProducts } = useBulkUnassignDefaultTag({
    onSuccess: () => refetchProducts(),
  })

  // Clear a product's default tag (removes it from this tag's inventory demand).
  const handleUnassign = useCallback(
    (row: any) => {
      if (!tagShopProductId) return
      unassignProducts({ tagShopProductId, product_ids: [row.id] })
    },
    [tagShopProductId, unassignProducts],
  )

  const confirmUnassign = useCallback(() => {
    if (unassignTarget) handleUnassign(unassignTarget)
    setUnassignTarget(null)
  }, [unassignTarget, handleUnassign])

  // Inventory object for the InfoBlock/table UI + the two settings modals, built
  // from the REAL persisted settings the threshold endpoint now returns (VERA-246)
  // rather than hardcoded defaults. `id` is the tag-shop product id — the key the
  // settings/auto-order mutations upsert on.
  const autoOrderVariant =
    threshold?.auto_order_qty != null
      ? tagProduct?.variants?.find((v: any) => v.quantity === threshold.auto_order_qty) ??
        tagProduct?.variants?.[0] ??
        null
      : tagProduct?.variants?.[0] ?? null

  const inventory = tagProduct
    ? {
        id: tagShopProductId,
        tag_shop_product: tagProduct,
        // POSSESSION, not the deprecated ordered counter (VERA-455) — the shared
        // InventoryLevelCell plots Ordered / Available / Threshold / Uncertified.
        at_vera_qty: threshold?.at_vera_qty ?? 0,
        incoming_qty: threshold?.incoming_qty ?? 0,
        available_qty: threshold?.available_qty ?? 0,
        capacity: threshold?.capacity ?? 0,
        threshold: threshold?.threshold ?? 0,
        threshold_type: threshold?.threshold_type ?? 'number',
        auto_order: threshold?.auto_order ?? false,
        auto_order_qty: threshold?.auto_order_qty ?? null,
        auto_order_variant: autoOrderVariant,
        auto_order_payment_method_id: threshold?.auto_order_payment_method_id ?? null,
        send_threshold_email: threshold?.send_threshold_email ?? false,
        for_products: products,
        // Bar demand = uncertified assigned products' in-stock quantity (not all
        // assigned) — certified products already have a tag.
        uncertified_qty: threshold?.uncertified_qty ?? 0,
      }
    : null

  // "Uncertified products" = Σ in-stock quantity of products assigned to this tag that are
  // not yet certified (they still need a tag). This is the gauge's demand.
  //
  // CAPACITY = Available − demand (VERA-455). It was `ordered − demand`, reading the
  // never-decremented lifetime purchase counter, which let this page report a comfortable
  // surplus for a SKU whose tags were all still sitting at Vera.
  const uncertifiedProducts = threshold?.uncertified_qty ?? 0
  const tagsOrdered = threshold?.at_vera_qty ?? 0 // paid for, still with Vera
  const capacity = threshold?.capacity ?? (threshold?.available_qty ?? 0) - uncertifiedProducts

  // Status mirrors the InventoryLevelCell bar (capacity vs threshold; critical wins) — and
  // now literally shares its derivation (`utils/inventory`, working-conventions §36) rather
  // than being the third hand-rolled copy of it. The `let status = ''` this replaces had the
  // same shape as the copy inside the gauge that threw on `INVENTORY_STATUSES[''].bar`; here
  // it survived only because `StatusChipCell` happens to tolerate an unknown status.
  const status = inventoryStatusFromCapacity(capacity, inventory?.threshold)

  // Column header with an info-icon tooltip (keeps the three tag columns consistent).
  const headerWithInfo = (label: string, tip: string) => () => (
    <Stack direction="row" spacing={1} alignItems="center">
      <span>{label}</span>
      <Tooltip arrow placement="top" title={tip}>
        <span style={{ display: 'inline-flex' }}>
          <InfoIcon strokeWidth={1} size={14} color={theme.palette.grey[600]} />
        </span>
      </Tooltip>
    </Stack>
  )

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Product',
        type: 'string',
        searchParams: { fields: ['title', 'sku'], preview: 'products' },
        renderCell: ({ row }: { row: any }) => <ProductCell product={row} />,
        // 300 = the ActionLine search field's measured rendered width on this page, so the
        // column's right border lines up with the search box above it instead of stopping
        // 50px short. Measured in the browser, not guessed.
        width: 300,
        pinned: 'left',
      },
      {
        field: 'type',
        headerName: 'Product type',
        type: 'string',
        width: 180,
        renderCell: ({ value }: { value: string | null }) =>
          value ? (
            <Box sx={{ pl: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <CategoryCell category={value} filter />
            </Box>
          ) : null,
      },
      {
        field: 'source',
        headerName: 'Source',
        type: 'string',
        width: 130,
        renderHeader: headerWithInfo('Source', 'Where the product synced from (e.g. Shopify), or Manual when added by hand.'),
        renderCell: ({ value }: { value: string | null }) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {value || 'Manual'}
            </Typography>
          </Box>
        ),
      },
      {
        // 140, up from 110, where the header truncated to "In sto…". A number column is
        // sized by its HEADER, not its values (same precedent as ProductsPage's Inventory
        // column): the title text plus the sort icon plus the title→icon gap plus the
        // grid's cell padding is the real minimum.
        field: 'quantity',
        headerName: 'In stock',
        type: 'number',
        width: 140,
      },
      // ── Certified / Uncertified, NOT Tagged / Untagged (VERA-455) ────────────────────
      // Same fact, previously three surfaces and two vocabularies: ProductsPage calls it
      // "Uncertified inventory" (`quantity − certificates_count`) and the Inventory tab says
      // "Uncertified products", while this page said "Untagged". Verified identical before
      // renaming: `get-vera-products-by-tag.use-case.ts` computes tagged = min(quantity,
      // certificate count) and untagged = max(0, quantity − certificate count), so a unit is
      // "tagged" exactly when it has a certificate. One fact, one word.
      {
        field: 'tagged',
        headerName: 'Certified',
        type: 'number',
        width: 130,
        renderHeader: headerWithInfo('Certified', 'In-stock units that already have a certificate.'),
        renderCell: ({ value }: { value: number }) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{ color: value > 0 ? theme.palette.success.main : 'text.secondary' }}
            >
              {value}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'untagged',
        headerName: 'Uncertified',
        type: 'number',
        width: 150,
        renderHeader: headerWithInfo(
          'Uncertified',
          'In-stock units that still need a tag. This is the demand.',
        ),
        renderCell: ({ value }: { value: number }) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: value > 0 ? 600 : 400, color: value > 0 ? theme.palette.warning.main : 'text.secondary' }}
            >
              {value}
            </Typography>
          </Box>
        ),
      },
      {
        // "Coverage", plain: with Certified and Uncertified beside it there is nothing left
        // to disambiguate, and it is shorter (§25).
        field: 'coverage',
        headerName: 'Coverage',
        sortable: false,
        width: 180,
        renderHeader: headerWithInfo('Coverage', 'Share of in-stock units that are certified.'),
        // % of in-stock units already certified. Derived from certified / in stock.
        renderCell: ({ row }: { row: any }) => {
          const pct = row.quantity > 0 ? Math.round((row.tagged / row.quantity) * 100) : 0
          return (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%', width: '100%' }}>
              <Box sx={{ position: 'relative', flexGrow: 1, height: 6, borderRadius: 3, bgcolor: theme.palette.grey[200], overflow: 'hidden' }}>
                <Box
                  sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
                    bgcolor: pct === 100 ? theme.palette.success.main : theme.palette.primary[400],
                    borderRadius: 3,
                  }}
                />
              </Box>
              <Typography variant="body3" color="text.secondary" sx={{ minWidth: 34, textAlign: 'right' }}>
                {pct}%
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: 'product_url',
        headerName: 'Storefront',
        sortable: false,
        width: 120,
        align: 'center',
        renderCell: ({ value }: { value: string | null }) =>
          value ? (
            <Box
              component="a"
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary[600],
                '&:hover': { color: theme.palette.primary[800] },
              }}
            >
              <ArrowUpRightIcon strokeWidth={1} size={18} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary"></Typography>
          ),
      },
      {
        field: 'actions',
        headerName: false,
        sortable: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }: { row: any }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    [],
  )

  const getActionItems = useCallback(
    (row: any): ActionDropdownItem[] => [
      {
        label: 'Details',
        onClick: () => navigate(ROUTES.PRODUCT(row.id), { state: referrer }),
        icon: <InfoIcon strokeWidth={1} />,
      },
      {
        label: 'Unassign',
        onClick: () => setUnassignTarget(row),
        icon: <Link2OffIcon strokeWidth={1} color={theme.palette.error.main} />,
        destructive: true,
      },
    ],
    [navigate, referrer, theme.palette.error.main],
  )

  const isLoading = isLoadingTag || isLoadingProducts || isLoadingThreshold

  if (isLoadingTag) {
    return (
      <>
        <Header
          title="Tag inventory"
          breadcrumbsOveride="Inventory"
          rightComponents={null}
        />
        <Stack spacing={6}>
          <Skeleton height={80} />
          <Skeleton height={400} />
        </Stack>
      </>
    )
  }

  if (!inventory || !tagProduct) {
    return (
      <>
        <Header
          title="Tag inventory"
          breadcrumbsOveride="Inventory"
          rightComponents={null}
        />
        <EmptyState
          title="No inventory data"
          text="Inventory information is not available yet. Connect your storefront to sync tag usage."
          icon={<Inventory sx={{ fontSize: 32 }} />}
          // The button carries the referrer: this is a dead-end empty state sending the user to
          // a different surface to fix it, so the way back to the inventory they were looking at
          // is the whole point of the trip.
          buttons={
            <Button variant="primary" onClick={() => navigate(ROUTES.SETTINGS_COMPANY_INTEGRATIONS, { state: referrer })}>
              Manage Integrations
            </Button>
          }
        />
      </>
    )
  }

  // One short sentence each, matching the Tags → Inventory cards word for word so a term
  // means the same thing on both surfaces (VERA-455). The monospace formula restatements
  // are gone: the gauge's own tooltip breaks the arithmetic out, beside the bar it plots.
  const uncertifiedFieldTip = 'Products in stock assigned to this tag that still need one.'
  const orderedTip = "Paid for, and Vera is still preparing them. Nothing has shipped yet."

  return (
    <>
      <Header
        avatar={{ url: tagProduct.images?.[0] ?? '', letters: (tagProduct.title ?? '').slice(0, 2) }}
        title={tagProduct.title}
        breadcrumbsOveride={tagProduct.title}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            <Button
              variant="secondary"
              startIcon={<ShoppingCartIcon strokeWidth={1} />}
              onClick={() => navigate(ROUTES.TAGS_SHOP_PRODUCT(sku as string), { state: referrer })}
            >
              Purchase tags
            </Button>
            <Button
              variant="secondary"
              startIcon={<PlusIcon strokeWidth={1} />}
              onClick={() => setOpenSetProductsForTagModal(true)}
            >
              Assign products
            </Button>
            <PermissionGate permission="tags_inventory_settings">
              <Button
                variant="secondary"
                startIcon={<Settings2Icon strokeWidth={1} />}
                onClick={() => setOpenInventorySettingsModal(true)}
              >
                Inventory settings
              </Button>
            </PermissionGate>
          </Stack>
        }
      />
      <Stack spacing={6}>
        {/* Inventory-levels bar (kept wider) with Status, Auto order, and the key
            counts spread across the block. Each field has an info icon; the bar keeps
            its own Tags/Products/Gap hover. Set/Assign open the respective modals. */}
        <InfoBlock>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ overflowX: 'auto' }}
          >
            <InfoItem
              label="Levels"
              tooltip="Tags you can use now versus the products still needing one. Green is a surplus, red is a shortfall."
              value={
                isLoadingThreshold ? (
                  <Skeleton height={28} width={360} />
                ) : (
                  // The bar in a 28px band so it centers on the same line as the
                  // Status/Auto-order chips beside it. The `compact` prop this used to pass
                  // is gone (VRG-397): the on-bar labels it suppressed no longer exist on
                  // any surface, so every render is what compact used to mean.
                  <Box sx={{ width: '100%', maxWidth: 340, display: 'flex', alignItems: 'center', minHeight: 28 }}>
                    <InventoryLevelCell inventory={inventory} />
                  </Box>
                )
              }
              // 340, down from 460. Adding the Available field made six items in a row whose
              // container is ~1000px at the working width, and the last two (Available, Auto
              // order) fell off the end into the block's overflow scroll. The bar
              // reads fine narrower; a field that is invisible does not.
              sx={{ width: 340, flexShrink: 0 }}
            />
            <InfoItem
              label="Status"
              tooltip="Plenty, Low, or Critical, from your capacity versus the alert threshold."
              value={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 28 }}>
                  <StatusChipCell status={status} statuses={INVENTORY_STATUSES} />
                </Box>
              }
              sx={{ width: 'auto', flexShrink: 0 }}
            />
            <InfoItem
              label="Uncertified products"
              tooltip={uncertifiedFieldTip}
              value={
                isLoadingThreshold ? (
                  <Skeleton width={30} height={20} />
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minHeight: 28 }}>
                    <Typography variant="body2">{uncertifiedProducts}</Typography>
                    <Button
                      variant="ghost"
                      onClick={() => setOpenSetProductsForTagModal(true)}
                    >
                      Assign
                    </Button>
                  </Stack>
                )
              }
              sx={{ width: 'auto', flexShrink: 0 }}
            />
            <InfoItem
              label="Ordered"
              tooltip={orderedTip}
              value={
                isLoadingThreshold ? (
                  <Skeleton width={30} height={20} />
                ) : (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 28 }}>
                    <Typography variant="body2">{tagsOrdered}</Typography>
                  </Box>
                )
              }
              sx={{ width: 'auto', flexShrink: 0 }}
            />
            {/* Available earns its own field: it is the number Capacity and the Status chip
                are both computed from, so leaving it off made both unexplainable. */}
            <InfoItem
              label="Available"
              tooltip="Tags in your hands. You can certify products with these today."
              value={
                isLoadingThreshold ? (
                  <Skeleton width={30} height={20} />
                ) : (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 28 }}>
                    <Typography variant="body2">{threshold?.available_qty ?? 0}</Typography>
                  </Box>
                )
              }
              sx={{ width: 'auto', flexShrink: 0 }}
            />
            <InfoItem
              label="Auto order"
              tooltip="Whether tags are automatically reordered when stock drops below the threshold."
              value={
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minHeight: 28 }}>
                  <StatusChipCell
                    status={inventory.auto_order ? 'on' : 'off'}
                    statuses={AUTO_ORDER_STATUSES}
                  />
                  <PermissionGate permission="tags_inventory_settings">
                    <Button
                      variant="ghost"
                      onClick={() => setOpenInventorySettingsModal(true)}
                    >
                      Set
                    </Button>
                  </PermissionGate>
                </Stack>
              }
              sx={{ width: 'auto', flexShrink: 0 }}
            />
          </Stack>
        </InfoBlock>
        <Table
          fillBottom
          topOffset={300}
          loading={isLoadingProducts}
          columns={columns}
          rows={products}
          emptyState={
            <EmptyState
              title="No products assigned yet"
              text="Use the Assign products action above to set this tag as the default tag for products"
              icon={<ShoppingBagIcon strokeWidth={1} size={32} />}
            />
          }
        />
      </Stack>
      <SetProductsForTagModal
        tagShopProductId={tagShopProductId ?? ''}
        open={openSetProductsForTagModal}
        onClose={() => setOpenSetProductsForTagModal(false)}
        refetch={refetch}
      />
      {/* Mounted only while open so each modal initialises from the loaded
          settings (an always-mounted modal captures stale pre-load state). */}
      {openInventorySettingsModal && (
        <SetInventorySettingsModal
          inventory={inventory}
          open={openInventorySettingsModal}
          onClose={() => setOpenInventorySettingsModal(false)}
          refetch={refetch}
        />
      )}
      <Modal
        title="Unassign product"
        open={!!unassignTarget}
        onClose={() => setUnassignTarget(null)}
        actions={
          <Stack direction="row" spacing={3} justifyContent="flex-end">
            <Button variant="secondary" onClick={() => setUnassignTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" destructive onClick={confirmUnassign}>
              Unassign
            </Button>
          </Stack>
        }
      >
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          Unassign <b>{unassignTarget?.title}</b> from this tag? It no longer counts
          toward this tag's demand. You can reassign it anytime.
        </Typography>
      </Modal>
    </>
  )
}
